import QRCode from 'qrcode';
import { useEffect, useRef, useState } from 'react';

import type { LeagueSheetLinkResponse } from '@league/shared/contract';
import { buttonClass } from '@league/ui';

import { apiFetch } from '../../lib/api';

/**
 * "SHOW QR CODE" — the score sheet's QR on the director's phone, for a venue with no printer
 * (owner, 2026-10-15). The same address the printed sheet carries, so a table that scans the phone
 * lands exactly where a table that scans paper does. Drawn from the QR's own modules rather than an
 * image, so it stays sharp at any size and needs no colour of its own.
 */
export function ShowScoreQr({ leagueId }: { leagueId: string }) {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState<LeagueSheetLinkResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const node = dialog.current;
    if (!node) return;
    if (open && !node.open) node.showModal();
    if (!open && node.open) node.close();
  }, [open]);

  useEffect(() => {
    if (!open || link) return;
    setFailed(false);
    apiFetch<LeagueSheetLinkResponse>(`/api/leagues/${encodeURIComponent(leagueId)}/score-sheet-link`)
      .then(setLink)
      .catch(() => setFailed(true));
  }, [open, link, leagueId]);

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        className={buttonClass('secondary')}
      >
        Show QR code
      </button>
      <dialog
        ref={dialog}
        aria-label="Score sheet QR code"
        onClose={() => setOpen(false)}
        onClick={(event) => {
          if (event.target === event.currentTarget) setOpen(false);
        }}
        className="m-auto w-[min(94vw,28rem)] rounded-lg border border-light bg-paper p-0 text-ink shadow-lg backdrop:bg-deep/40"
      >
        <div className="px-5 pb-6 pt-4">
          <div className="flex items-start justify-between gap-3">
            <h2 className="pt-1 font-display text-[20px] leading-tight">{link?.leagueName ?? 'Score sheet'}</h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="-mr-2 flex min-h-[44px] min-w-[44px] items-center justify-center text-[20px] text-ink-2 hover:text-ink"
            >
              ✕
            </button>
          </div>
          {failed ? (
            <p role="alert" className="mt-4 text-[15px] text-ink-2">
              The QR code didn't load. Try again.
            </p>
          ) : link ? (
            <QrSvg url={link.url} />
          ) : (
            <div className="mt-4 aspect-square w-full" aria-busy="true" />
          )}
        </div>
      </dialog>
    </>
  );
}

function QrSvg({ url }: { url: string }) {
  const qr = QRCode.create(url, { errorCorrectionLevel: 'M' });
  const size = qr.modules.size;
  const quiet = 4;
  const cells: string[] = [];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (qr.modules.get(x, y)) cells.push(`M${x + quiet} ${y + quiet}h1v1h-1z`);
    }
  }
  const box = size + quiet * 2;
  return (
    <svg
      role="img"
      aria-label="QR code for tonight's scores"
      data-url={url}
      viewBox={`0 0 ${box} ${box}`}
      shapeRendering="crispEdges"
      className="mt-4 aspect-square w-full bg-paper text-ink"
    >
      <path d={cells.join('')} fill="currentColor" />
    </svg>
  );
}
